import * as React from "react";
import { Form, Table, Checkbox, Accordion, Icon } from "semantic-ui-react";
import styles from "./Permissions.module.scss";

export type PermissionsProps = {
  permissions: Api.Permission[];
  userGroups: Api.UserGroup[];
  onChanged: (permissions: Api.Permission[]) => void;
  disabled?: boolean;
};

type PermissionsState = {
  isOpen: boolean;
};

/** Permissions component */
class Permissions extends React.Component<PermissionsProps, PermissionsState> {
  state = { isOpen: false };

  render(): JSX.Element {
    const { permissions, userGroups, disabled } = this.props;
    const { isOpen } = this.state;
    const setPermission = disabled === undefined ? false : disabled;
    return (
      <Form.Group grouped={true} id="open-permissions">
        <Form.Field>
          <Accordion fluid={true} styled={true}>
            <Accordion.Title
              active={isOpen}
              onClick={() => this.setState(() => ({ isOpen: !isOpen }))}
            >
              <Icon name="dropdown" />
              <label>
                <b>Permissions</b>
              </label>
            </Accordion.Title>
            <Accordion.Content active={isOpen}>
              <Table compact="very" className={styles.table}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Group</Table.HeaderCell>
                    <Table.HeaderCell>Read</Table.HeaderCell>
                    <Table.HeaderCell>Write</Table.HeaderCell>
                    <Table.HeaderCell>Execute</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {userGroups.map(userGroup => {
                    const foundPermission = permissions.find(
                      permission => permission.userGroup.id === userGroup.id
                    );
                    let groupPermission: Api.Permission = {
                      read: false,
                      write: false,
                      execute: false,
                      userGroup: {
                        id: userGroup.id,
                        enumName: userGroup.enumName,
                        url: userGroup.url
                      }
                    };
                    if (foundPermission) {
                      groupPermission = { ...foundPermission };
                    }
                    return (
                      <Table.Row key={userGroup.id}>
                        <Table.Cell>{userGroup.enumName}</Table.Cell>
                        <Table.Cell>
                          <Checkbox
                            id={`${userGroup.enumName}-read`}
                            checked={groupPermission.read}
                            onChange={() => {
                              groupPermission.read = !groupPermission.read;
                              this.handlePermissionsChanged({ ...groupPermission });
                            }}
                            disabled={setPermission}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Checkbox
                            id={`${userGroup.enumName}-write`}
                            checked={groupPermission.write}
                            onChange={() => {
                              groupPermission.write = !groupPermission.write;
                              this.handlePermissionsChanged({ ...groupPermission });
                            }}
                            disabled={setPermission}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Checkbox
                            id={`${userGroup.enumName}-execute`}
                            checked={groupPermission.execute}
                            onChange={() => {
                              groupPermission.execute = !groupPermission.execute;
                              this.handlePermissionsChanged({ ...groupPermission });
                            }}
                            disabled={setPermission}
                          />
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </Accordion.Content>
          </Accordion>
        </Form.Field>
      </Form.Group>
    );
  }

  private handlePermissionsChanged = (changedPermission: Api.Permission): void => {
    // eslint-disable-next-line prefer-const
    let { permissions, onChanged } = this.props;
    const { read, write, execute } = changedPermission;
    changedPermission.read = write ? true : read;
    const hasPermissions = read || write || execute;
    const foundPermission = permissions.find(
      permission => permission.userGroup.id === changedPermission.userGroup.id
    );
    if (hasPermissions && foundPermission) {
      permissions = permissions.map(permission => {
        if (permission.userGroup.id === changedPermission.userGroup.id) {
          return { ...changedPermission };
        } else {
          return permission;
        }
      });
    } else if (hasPermissions && !foundPermission) {
      permissions = [...permissions, changedPermission];
    } else if (!hasPermissions && foundPermission) {
      permissions = permissions.filter(
        permission => permission.userGroup.id !== changedPermission.userGroup.id
      );
    }

    const newPermissions = permissions.map(permission => {
      const userGroup = {
        id: permission.userGroup.id,
        url: permission.userGroup.url,
        enumName: permission.userGroup.enumName
      };
      return {
        userGroup: userGroup,
        read: permission.read,
        write: permission.write,
        execute: permission.execute
      };
    });

    newPermissions.sort((a, b) => a.userGroup.id - b.userGroup.id);
    onChanged(newPermissions);
  };
}

export default Permissions;
